import React, { Component } from "react";
import PropTypes from "prop-types";
import ManuscriptAddBlock from "./ManuscriptAddBlock";
import ManuscriptBlock from "./ManuscriptBlock";
import { BlockDefaults } from "./BlockTypes";

/**
 * ManuscriptContentView: props => React element
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Content view of a manuscript document in the editor,
 * renders blocks
 *
 * @param {*} props
 */
class ManuscriptContentView extends Component {
  /**
   * onUpdateDocument = updatedBlocks => ()
   * Updates the document with new content blocks
   */
  onUpdateDocument = updatedBlocks => {
    this.props.updateDocument(updatedBlocks);
  };

  overrideReadOnlyMode = true;

  /**
   * insertContentBlock = content, atIndex => ()
   * Inserts a content block at the specified index and updates the document.
   */
  insertContentBlock = (content, atIndex) => {
    if (this.props.isReadOnlyMode) return;

    const blocks = [...this.props.contentBlocks];
    const index = atIndex || blocks.length;

    blocks.splice(index, 0, content);

    this.onUpdateDocument(blocks);
  };

  /**
   * removeContentBlock = atIndex => ()
   * Removes the content block at the specified index and updates the document.
   */
  removeContentBlock = atIndex => {
    if (atIndex === null) return;
    if (this.props.isReadOnlyMode) return;

    const blocks = [...this.props.contentBlocks];
    blocks.splice(atIndex, 1);

    this.onUpdateDocument(blocks);
  };

  /**
   * updateContentBlock = atIndex, updatedBlock => ()
   * Updates the content block at the specified position using a new version
   * and updates the document.
   */
  updateContentBlock = (atIndex, updatedBlock) => {
    if (atIndex === null || updatedBlock === null) return;
    if (this.props.isReadOnlyMode && !this.overrideReadOnlyMode) return;

    const blocks = [...this.props.contentBlocks];
    blocks[atIndex] = updatedBlock;

    this.onUpdateDocument(blocks);
  };

  /**
   * moveContentBlock = fromIndex, toIndex => ()
   * Moves the content block at the specified index to a new index
   * and updates the document.
   */
  moveContentBlock = (fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null) return;
    if (this.props.isReadOnlyMode) return;

    const blocks = [...this.props.contentBlocks];
    const block = blocks[fromIndex];

    let newIndex = toIndex;

    if (newIndex > blocks.count) newIndex = blocks.count - 1;
    if (newIndex < 0) newIndex = 0;

    if (newIndex !== fromIndex) {
      blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, block);

      this.onUpdateDocument(blocks);
    }
  };

  /**
   * createNewContentBlock = type, atIndex => ()
   * Creates a new content block with the specified block type at
   * a given index and updates the document.
   */
  createNewContentBlock = (type, atIndex) => {
    let defaultProperties = {};
    if (BlockDefaults.hasOwnProperty(type)) {
      defaultProperties = BlockDefaults[type];
    }
    const block = {
      type,
      ...defaultProperties,
      id: this.generateContentBlockIdentifier()
    };

    this.insertContentBlock(block, atIndex);
  };

  /**
   * generateContentBlockIdentifier = () => String
   * Generates a random identifier string to use as a block ID.
   */
  generateContentBlockIdentifier = () => btoa(Math.random()).substr(5, 5);

  render() {
    if (this.props.contentBlocks.length === 0) {
      return this.props.isReadOnlyMode ? null : (
        <ManuscriptAddBlock onClick={this.createNewContentBlock} />
      );
    }
    return (
      <div
        className={`ed-manuscript-content${
          this.props.isReadOnlyMode ? " render" : ""
        }`}
      >
        {this.props.contentBlocks.map((block, index) => (
          <ManuscriptBlock
            key={block.id}
            block={block}
            onDelete={() => this.removeContentBlock(index)}
            onInsert={type => this.createNewContentBlock(type, index + 1)}
            onMove={moveBy => this.moveContentBlock(index, index + moveBy)}
            onChange={updatedBlock =>
              this.updateContentBlock(index, updatedBlock)
            }
            isFirstElement={index === 0}
            isLastElement={index === this.props.contentBlocks.length - 1}
            isReadOnlyMode={this.props.isReadOnlyMode}
          />
        ))}
      </div>
    );
  }
}

ManuscriptContentView.propTypes = {
  updateDocument: PropTypes.func.isRequired,

  contentBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired
    })
  ).isRequired,

  isReadOnlyMode: PropTypes.bool.isRequired
};

export default ManuscriptContentView;



// WEBPACK FOOTER //
// ./src/modules/editor/manuscript/ManuscriptContentView.js